const PREFERRED_MIME_TYPE = "audio/webm;codecs=opus";
// 24k left quiet speech audibly coarse, which an ASR no-speech gate can read as silence. Opus
// mono is clean here, and a few KB per clip costs nothing against a failed transcription.
const AUDIO_BITS_PER_SECOND = 32_000;

export interface ClipRecorder {
  readonly emitted: number;
  // Ends the current clip and resolves once its blob has been dealt with, returning the total
  // number of clips emitted this session. Async because MediaRecorder.stop() returns before its
  // final `dataavailable` fires — a synchronous count could be one short, and one short means
  // dropping the rep's last sentence, which is where the instruction usually is.
  //
  // `emit: false` discards the blob instead of emitting it. That is how a clip containing no
  // speech is dropped: MIN_CLIP_MS is a floor on clip LENGTH and discards nothing on its own, and
  // a muted mic would otherwise ship 55-second clips of pure silence for the server to reject one
  // 409 at a time.
  flush(emit?: boolean): Promise<number>;
  open(): void;
}

const createRecorder = (stream: MediaStream): MediaRecorder => {
  // isTypeSupported has historically returned optimistic answers in WebKit, so a rejected
  // construction falls back to whatever the browser will give us — the backend accepts
  // WebM/Opus, Ogg/Opus and AAC in MP4 alike.
  if (MediaRecorder.isTypeSupported(PREFERRED_MIME_TYPE)) {
    try {
      return new MediaRecorder(stream, {
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
        mimeType: PREFERRED_MIME_TYPE,
      });
    } catch {
      // fall through
    }
  }
  return new MediaRecorder(stream, { audioBitsPerSecond: AUDIO_BITS_PER_SECOND });
};

const createClipRecorder = ({
  onClip,
  stream,
}: {
  onClip: (seq: number, blob: Blob) => void;
  stream: MediaStream;
}): ClipRecorder => {
  let recorder: MediaRecorder | undefined;
  let emitted = 0;
  let pending: Promise<void> | undefined;
  // Read by ondataavailable, set by flush() before it calls stop(). A field rather than an
  // argument because the event fires long after the call that caused it.
  let emitCurrent = true;

  const open = () => {
    recorder = createRecorder(stream);
    emitCurrent = true;
    let resolvePending = () => {};
    pending = new Promise<void>((resolve) => {
      resolvePending = resolve;
    });

    recorder.ondataavailable = (event) => {
      // Seq is assigned here and only here, so `emitted` is exactly the number of onClip calls.
      // Increment it anywhere else — at cut-decision time, or per constructed recorder including
      // a discarded one — and the slot map has a permanent hole the drain waits on forever.
      if (event.data.size > 0 && emitCurrent) {
        onClip(emitted, event.data);
        emitted += 1;
      }
      resolvePending();
    };

    // No timeslice: one blob per recorder, so every clip is independently decodable.
    recorder.start();
  };

  // Overlapping flushes are normal traffic, not misuse: a silence cut fires from the rAF loop
  // while the rep presses Done in the same tick. stop() flips state to "inactive" synchronously,
  // so an unserialized second call reads the pre-clip `emitted` and reports one clip too few —
  // exactly one lost sentence, at the moment reps are most likely to press Done. Chaining also
  // stops a concurrent call from overwriting emitCurrent under the in-flight clip.
  let inFlight: Promise<number> | undefined;

  const flushCurrent = async (emit: boolean): Promise<number> => {
    if (!recorder || recorder.state === "inactive") {
      return emitted;
    }
    emitCurrent = emit;
    const settled = pending;
    recorder.stop();
    await settled;
    return emitted;
  };

  const flush = (emit = true): Promise<number> => {
    // The queued call runs against whatever recorder is open by the time it gets its turn, so
    // audio the rep produced between the cut and pressing Done is still captured.
    const chain = (inFlight ?? Promise.resolve(emitted)).then(() => flushCurrent(emit));
    // A rejected link must not poison every later flush, so the stored tail never rejects.
    inFlight = chain.catch(() => emitted);
    return chain;
  };

  return {
    get emitted() {
      return emitted;
    },
    flush,
    open,
  };
};

export default createClipRecorder;
